import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock, ArrowLeft, ChevronDown, CarIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

// UI Components
import PageLayout from '@/components/PageLayout';
import MediaUploader from '@/components/MediaUploader';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Contexts
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { useI18n } from '@/i18n/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthProvider';

// Geolocation
import { getCurrentLocation, LocationData } from '@/lib/geolocation';

// AI Assistant components
import AIQuestionFlow from '@/components/AIQuestionFlow';
import SymptomProcessingOverlay from '@/components/SymptomProcessingOverlay';

// Import new types and schemas
import { Phase2ResponseSchema } from '@/lib/schemas/ai';
import { useSymptomMedia } from '@/hooks/useSymptomMedia';
import { CarIssue } from '@/contexts/CarContext';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';

// Type for the AI analysis response (updated to match DB schema)
interface AIAnalysisResponse {
  problems_stated: string;  // renamed from summary
  questions: string[];
  possible_causes: string[];  // renamed from causes
  recommendations: string[];
  urgency: number;
  diy_check: string | string[];  // renamed from diyCheck
  explanation: string;  // added
  symptomId?: string;  // added - returned from finalize-analysis
  // New fields from JSON response
  date?: string;
  time?: string;
  location?: string;
  urgencyExplanation?: string;
  disclaimer?: string;
  urgency_label?: string;
  drivingSafety?: string;
  title?: string;
}

const LogSymptomPage = () => {
  const { cars, addIssue, addIssueToCar } = useCar();
  const { currentCar, currentCarId, selectCar } = useCarSelectionContext();
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Add the media hook
  const { uploaded, upload, loading: uploading, cleanup } = useSymptomMedia();
  
  // Get user location on component mount
  useEffect(() => {
    const getUserLocation = async () => {
      try {
        setIsGettingLocation(true);
        const location = await getCurrentLocation();
        setUserLocation(location);
        console.log('📍 User location detected:', location);
      } catch (error) {
        console.warn('⚠️ Could not get user location:', error);
        // Fallback to default location will be handled in the backend
      } finally {
        setIsGettingLocation(false);
      }
    };
    
    getUserLocation();
  }, []);
  
  const [description, setDescription] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAIFlow, setShowAIFlow] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false); // Add Phase 2 loading state
  const [processingStep, setProcessingStep] = useState<string>('reading');
  const [summaryProcessingStep, setSummaryProcessingStep] = useState<string>('readingAnswers'); // For summary generation overlay
  const [showSummaryProcessingOverlay, setShowSummaryProcessingOverlay] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [savedIssueId, setSavedIssueId] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  
  // Date and time state
  const [useCurrentTime, setUseCurrentTime] = useState(true);
  const [customDate, setCustomDate] = useState<Date | undefined>(new Date());
  const [customTime, setCustomTime] = useState('12:00');
  
  // Geolocation state
  const [userLocation, setUserLocation] = useState<LocationData | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  
  // AI flow responses (updated to match DB schema)
  const [aiResponses, setAiResponses] = useState<{
    problems_stated: string;  // renamed from summary
    questions: string[];
    answers: string[];
    possible_causes?: string[];  // renamed from causes
    recommendations?: string[];
    urgency?: number;
    diy_check?: string | string[];  // renamed from diyCheck
    explanation?: string;  // added
    imageObservations?: string;
    symptomId?: string; // Added symptomId
    visionAnalysis?: any; // structured Phase 1 output
    // New fields from JSON response
    date?: string;
    time?: string;
    location?: string;
    urgencyExplanation?: string;
    disclaimer?: string;
    urgency_label?: string;
    drivingSafety?: string;
    title?: string;
  }>({
    problems_stated: '',  // renamed from summary
    questions: [],
    answers: Array(7).fill(''),
  });
  
  const isSubmitDisabled = !description && imageFiles.length === 0 && videoFiles.length === 0;
  
  const selectedCar = currentCar;

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []); // Remove cleanup from dependencies to prevent infinite loop

  // Simple language detection function
  const detectLanguage = (text: string): string => {
    const latvianChars = /[āčēģīķļņšūž]/i;
    const romanianChars = /[ăâîșț]/i;
    const cyrillicChars = /[а-яё]/i;
    
    if (latvianChars.test(text)) return 'lv';
    if (romanianChars.test(text)) return 'ro';
    if (cyrillicChars.test(text)) return 'ru';
    
    // Default to English if no specific characters detected
    return 'en';
  };
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle ESC key, arrow keys, and zoom for full screen image
  useEffect(() => {
    if (!fullScreenImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullScreenImage(null);
        setCurrentImageIndex(-1);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowLeft' && currentImageIndex > 0) {
        const newIndex = currentImageIndex - 1;
        setCurrentImageIndex(newIndex);
        setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowRight' && currentImageIndex < imageFiles.length - 1) {
        const newIndex = currentImageIndex + 1;
        setCurrentImageIndex(newIndex);
        setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'unset';
    };
  }, [fullScreenImage, currentImageIndex, imageFiles]);

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    const availableSlots = 3 - imageFiles.length;
    const filesToAdd = files.slice(0, availableSlots);
    setImageFiles([...imageFiles, ...filesToAdd]);
    e.target.value = '';
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = [...imageFiles];
    newFiles.splice(index, 1);
    setImageFiles(newFiles);
  };
  
  // Function to get AI analysis from Supabase Edge Function (Phase 1: Questions)
  const getAIAnalysis = async (
    symptomDescription: string,
    imageUrls?: string[],
    imageMeta?: Array<{ filename: string; gcsPath: string }>
  ): Promise<{ questions: string[], imageObservations: string, visionAnalysis?: any }> => {
    setIsLoadingQuestions(true);
    
    try {
      console.log("🔄 Phase 1: Calling analyze-symptom for question generation");
      
      const carDetails = selectedCar ? {
        brand: selectedCar.brand,
        model: selectedCar.model,
        year: selectedCar.year,
        vin: selectedCar.vin,
        transmission: selectedCar.transmission,
        lastServiceDate: selectedCar.lastServiceDate,
        licensePlate: selectedCar.licensePlate,
        fuelType: selectedCar.fuelType,
        winterTiresBrand: selectedCar.winterTiresBrand,
        winterTiresSize: selectedCar.winterTiresSize,
        summerTiresBrand: selectedCar.summerTiresBrand,
        summerTiresSize: selectedCar.summerTiresSize
      } : null;
      
      // Detect language from the description
      const detectedLanguage = detectLanguage(symptomDescription);
      // Use detected language for AI if it's a supported language (en/lv), otherwise use user's preference
      // This ensures: if user types in English, AI responds in English; if they type in Latvian, AI responds in Latvian
      // Note: UI language always uses user's saved preference (from 'language' variable), only AI uses detected language
      const userLanguage = (detectedLanguage === 'en' || detectedLanguage === 'lv') ? detectedLanguage : language;
      
      console.log('🌍 Language detection:', {
        detected: detectedLanguage,
        userSetting: language,
        final: userLanguage
      });
      
      const { data, error } = await supabase.functions.invoke('analyze-symptom', {
        body: {
          description: symptomDescription,
          carDetails,
          imageUrls: imageUrls || [],
          imageMeta: imageMeta || [],
          language: userLanguage,
          userLocation: userLocation // Include user's actual location
        }
      });
      
      if (error) {
        console.error("Error calling analyze-symptom (Phase 1):", error);
        toast({
          title: t('logSymptom.errors.aiAnalysisFailed') || 'AI analysis failed',
          description: t('logSymptom.errors.aiAnalysisFailedDesc') || 'Could not generate questions. Using alternative questions.',
          variant: "destructive"
        });
        const fallback = generateFallbackAnalysis(symptomDescription);
        return {
          questions: fallback.questions,
          imageObservations: t('logSymptom.fallback.noImagesToAnalyze') || 'No images to analyze',
        };
      }
      
      console.log("Phase 1 response:", data);
      
      if (!data.questions || data.questions.length === 0) {
        console.warn("Invalid response format from analyze-symptom function (Phase 1)");
        const fallback = generateFallbackAnalysis(symptomDescription);
        return {
          questions: fallback.questions,
          imageObservations: t('logSymptom.fallback.noImagesToAnalyze') || 'No images to analyze',
        };
      }
      
      return {
        questions: data.questions,
        imageObservations:
          data.imageObservations || (t('logSymptom.fallback.noImagesToAnalyze') || 'No images to analyze'),
        visionAnalysis: data.visionAnalysis
      };
    } catch (err) {
      console.error("Exception when calling analyze-symptom (Phase 1):", err);
      toast({
        title: t('logSymptom.errors.aiAnalysisFailed') || 'AI analysis failed',
        description: t('logSymptom.errors.aiAnalysisFailedDesc') || 'Could not generate questions. Using alternative questions.',
        variant: "destructive"
      });
      const fallback = generateFallbackAnalysis(symptomDescription);
      return {
        questions: fallback.questions,
        imageObservations: t('logSymptom.fallback.noImagesToAnalyze') || 'No images to analyze',
      };
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Function to get AI summary synthesis (Phase 2)
  const getAISummary = async (
    symptomDescription: string, 
    questions: string[], 
    answers: string[], 
    imageObservations: string,
    visionAnalysis?: any
  ): Promise<AIAnalysisResponse> => {
    try {
      console.log("🔄 Phase 2: Calling finalize-analysis for summary synthesis");
      
      const carDetails = selectedCar ? {
        brand: selectedCar.brand,
        model: selectedCar.model,
        year: selectedCar.year,
        vin: selectedCar.vin,
        transmission: selectedCar.transmission,
        lastServiceDate: selectedCar.lastServiceDate,
        licensePlate: selectedCar.licensePlate,
        fuelType: selectedCar.fuelType,
        winterTiresBrand: selectedCar.winterTiresBrand,
        winterTiresSize: selectedCar.winterTiresSize,
        summerTiresBrand: selectedCar.summerTiresBrand,
        summerTiresSize: selectedCar.summerTiresSize
      } : null;
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const detectedLanguage = detectLanguage(symptomDescription);
      // Use detected language for AI if it's a supported language (en/lv), otherwise use user's preference
      // This ensures: if user types in English, AI responds in English; if they type in Latvian, AI responds in Latvian
      // Note: UI language always uses user's saved preference (from 'language' variable), only AI uses detected language
      const userLanguage = (detectedLanguage === 'en' || detectedLanguage === 'lv') ? detectedLanguage : language;
      
      const { data, error } = await supabase.functions.invoke('finalize-analysis', {
        body: {
          description: symptomDescription,
          carDetails,
          questions,
          answers,
          imageObservations,
          visionAnalysis,
          language: userLanguage,
          carId: currentCarId,
          userId: user.id,
          draftMedia: uploaded,  // Pass the uploaded media for linking
          userLocation: userLocation // Include user's actual location
        }
      });
      
      if (error) {
        console.error("Error calling finalize-analysis (Phase 2):", error);
        console.error("Error details:", error);
        throw new Error(`Phase 2 failed: ${error.message}`);
      }
      
      console.log("Phase 2 response:", data);
      
      // Return the structured response from the Edge Function
      return {
        problems_stated: data.problems_stated,
        questions: data.questions,
        possible_causes: data.possible_causes,
        recommendations: data.recommendations,
        urgency: data.urgency,
        diy_check: data.diy_check,
        explanation: data.explanation,
        symptomId: data.symptomId,
        // New fields from Phase 2
        date: data.date,
        time: data.time,
        location: data.location,
        urgencyExplanation: data.urgencyExplanation,
        disclaimer: data.disclaimer,
        urgency_label: data.urgency_label,
        drivingSafety: data.drivingSafety,
        title: data.title
      };
    } catch (err) {
      console.error("Exception when calling finalize-analysis (Phase 2):", err);
      throw err;
    }
  };
  
  // Fallback function if AI analysis fails
  const generateFallbackAnalysis = (description: string): AIAnalysisResponse => {
    const detectedLanguage = detectLanguage(description);
    const safeLang = detectedLanguage === 'lv' ? 'lv' : 'en';
    // Extract keywords from description to provide more contextual summaries
    const keywords = {
      engine: ['engine', 'start', 'stall', 'idle', 'power', 'acceleration', 'hesitation'],
      brakes: ['brake', 'stop', 'squeaking', 'grinding', 'pedal', 'abs'],
      transmission: ['gear', 'shift', 'automatic', 'manual', 'clutch', 'transmission'],
      electrical: ['light', 'battery', 'radio', 'window', 'electronics', 'fuse', 'electrical'],
      suspension: ['bumpy', 'ride', 'suspension', 'shock', 'strut', 'bounce', 'steering', 'wheel'],
      cooling: ['overheat', 'temperature', 'cooling', 'radiator', 'fan', 'thermostat'],
      exhaust: ['smell', 'smoke', 'exhaust', 'emission', 'muffler', 'catalytic']
    };
    
    const lowerDesc = description.toLowerCase();
    
    // Determine which system might be involved based on keywords
    let problemsStated = t(`logSymptom.fallback.needMore.${safeLang}`) || (safeLang === 'lv'
      ? 'Man vajag vairāk informācijas, lai labāk saprastu šo problēmu'
      : "I'll need more information to better understand this issue");
    for (const [system, terms] of Object.entries(keywords)) {
      if (terms.some(term => lowerDesc.includes(term))) {
        switch(system) {
          case 'engine':
            problemsStated =
              t(`logSymptom.fallback.problems.engine.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas izklausās pēc problēmas ar dzinēju vai degvielas sistēmu'
                : 'This appears to be related to the engine or fuel system');
            break;
          case 'brakes':
            problemsStated =
              t(`logSymptom.fallback.problems.brakes.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas izklausās pēc problēmas bremžu sistēmā'
                : 'This sounds like an issue with the braking system');
            break;
          case 'transmission':
            problemsStated =
              t(`logSymptom.fallback.problems.transmission.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas varētu būt saistīts ar transmisiju vai piedziņu'
                : 'This could be related to the transmission or drivetrain');
            break;
          case 'electrical':
            problemsStated =
              t(`logSymptom.fallback.problems.electrical.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas liecina par problēmu elektrosistēmā'
                : 'This suggests an electrical system problem');
            break;
          case 'suspension':
            problemsStated =
              t(`logSymptom.fallback.problems.suspension.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas varētu būt saistīts ar piekari vai stūres sistēmu'
                : 'This might be an issue with the suspension or steering system');
            break;
          case 'cooling':
            problemsStated =
              t(`logSymptom.fallback.problems.cooling.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas liecina par iespējamu dzesēšanas sistēmas problēmu'
                : 'This indicates a possible cooling system problem');
            break;
          case 'exhaust':
            problemsStated =
              t(`logSymptom.fallback.problems.exhaust.${safeLang}`) ||
              (safeLang === 'lv'
                ? 'Tas varētu būt saistīts ar izplūdes sistēmu'
                : 'This could be related to the exhaust system');
            break;
        }
      }
    }
    
    // Generate follow-up questions based on the description in the detected language
    const questions = generateFollowUpQuestions(description, detectedLanguage);
    
    return {
      problems_stated: problemsStated,
      questions,
      possible_causes: [
        t(`logSymptom.fallback.possibleCauses.${safeLang}`) ||
          (safeLang === 'lv'
            ? 'Neizdevās ģenerēt detalizētu iemeslu analīzi'
            : 'Could not generate detailed causes analysis'),
      ],
      recommendations: [
        t(`logSymptom.fallback.recommendations.${safeLang}`) ||
          (safeLang === 'lv'
            ? 'Ieteicams konsultēties ar servisu detalizētai diagnostikai'
            : 'Consider consulting with a mechanic for detailed diagnosis'),
      ],
      urgency: 3,
      diy_check:
        t(`logSymptom.fallback.diyCheck.${safeLang}`) ||
        (safeLang === 'lv'
          ? 'Pārbaudiet šķidrumu līmeņus un riepu spiedienu'
          : 'Check basic fluid levels and tire pressure'),
      explanation:
        t(`logSymptom.fallback.explanation.${safeLang}`) ||
        (safeLang === 'lv'
          ? 'Šī ir rezerves analīze AI apstrādes problēmu dēļ'
          : 'This is a fallback analysis due to AI processing issues'),
    };
  };
  
  // Generate context-aware follow-up questions based on the description
  const generateFollowUpQuestions = (description: string, language: string = 'en'): string[] => {
    const lowerDesc = description.toLowerCase();
    const commonQuestions = {
      en: [
      "When did you first notice this issue?",
      "Does the problem occur all the time or only sometimes?",
      "Have you noticed any dashboard warning lights?"
      ],
      lv: [
        "Kad jūs pirmo reizi pamanījāt šo problēmu?",
        "Vai problēma rodas visu laiku vai tikai dažreiz?",
        "Vai esat pamanījuši kādas brīdinājuma gaismas uz instrumentu panelis?"
      ]
    };
    
    const safeLang = language === 'lv' ? 'lv' : 'en';
    const questions = commonQuestions[safeLang];
    
    const contextualQuestions = [];
    
    // Multilingual noise/sound related questions
    if (lowerDesc.includes('noise') || lowerDesc.includes('sound') || 
        lowerDesc.includes('knock') || lowerDesc.includes('squeal') || 
        lowerDesc.includes('rattle') || lowerDesc.includes('trokšņi') || 
        lowerDesc.includes('skaņa') || lowerDesc.includes('zveka') ||
        lowerDesc.includes('zgomot') || lowerDesc.includes('sunet')) {
      
      const noiseQuestions = {
        en: [
        "When do you hear the noise? (During acceleration, braking, turning, etc.)",
        "How would you describe the sound? (Knocking, squealing, grinding, etc.)",
        "Does the noise change with vehicle speed?"
        ],
        lv: [
          "Kad jūs dzirdat trokšņi? (Paātrinājuma laikā, bremzējot, griežoties, utt.)",
          "Kā jūs aprakstītu skaņu? (Klauvēšana, čīkstēšana, slīpēšana, utt.)",
          "Vai trokšņi mainās atkarībā no transportlīdzekļa ātruma?"
        ]
      };
      
      contextualQuestions.push(...noiseQuestions[safeLang]);
    }
    
    // Add performance related questions
    if (lowerDesc.includes('power') || lowerDesc.includes('acceleration') || 
        lowerDesc.includes('slow') || lowerDesc.includes('performance')) {
      contextualQuestions.push(
        ...(safeLang === 'lv'
          ? [
              'Vai problēma parādās noteiktos apgriezienu diapazonos?',
              'Vai esat pamanījuši izmaiņas degvielas patēriņā?',
              'Vai paātrinoties auto raustās vai “rauj”?',
            ]
          : [
              'Does the issue happen at specific RPM ranges?',
              'Have you noticed any change in fuel consumption?',
              'Does the vehicle hesitate or jerk during acceleration?',
            ])
      );
    }
    
    // Add fluid/leak related questions
    if (lowerDesc.includes('leak') || lowerDesc.includes('fluid') || 
        lowerDesc.includes('oil') || lowerDesc.includes('water')) {
      contextualQuestions.push(
        ...(safeLang === 'lv'
          ? [
              'Kādā krāsā ir šķidrums, kas tek?',
              'No kurienes, šķiet, nāk noplūde?',
              'Kad pamanāt noplūdi? (Pēc stāvēšanas, braucot, u.c.)',
            ]
          : [
              "What color is the fluid that's leaking?",
              'Where does the leak appear to be coming from?',
              'When do you notice the leak? (After parking, while driving, etc.)',
            ])
      );
    }
    
    // Add vibration related questions
    if (lowerDesc.includes('vibration') || lowerDesc.includes('shake') || 
        lowerDesc.includes('shaking') || lowerDesc.includes('wobble')) {
      contextualQuestions.push(
        ...(safeLang === 'lv'
          ? [
              'Pie kāda ātruma parādās vibrācija?',
              'Vai vibrācija nāk no konkrētas vietas? (Stūre, sēdeklis u.c.)',
              'Vai vibrācija mainās bremzējot vai paātrinoties?',
            ]
          : [
              'At what speed does the vibration occur?',
              'Does the vibration come from a specific area? (Steering wheel, seat, etc.)',
              'Does the vibration change when braking or accelerating?',
            ])
      );
    }
    
    // Add starting/stalling related questions
    if (lowerDesc.includes('start') || lowerDesc.includes('stall') || 
        lowerDesc.includes('die') || lowerDesc.includes('crank')) {
      contextualQuestions.push(
        ...(safeLang === 'lv'
          ? [
              'Vai starteris griež, bet dzinējs neiedarbojas, vai arī starteris vispār negriež?',
              'Vai problēma ir tikai aukstam vai arī siltam dzinējam?',
              'Vai esat mēģinājuši piepīpēt (jump-start)?',
            ]
          : [
              'Does the engine crank but not start, or not crank at all?',
              'Does the issue happen only when the engine is cold or hot?',
              'Have you tried jump-starting the vehicle?',
            ])
      );
    }
    
    // Combine common and contextual questions, ensure we have 5-7 questions
    const allQuestions = [...questions, ...contextualQuestions];
    
    // If we still don't have enough questions, add some general ones
    const generalQuestions = {
      en: [
      "Has the vehicle been serviced recently?",
      "Has this issue happened before?",
      "Does the issue happen in specific weather conditions?",
      "Are there any other symptoms you've noticed?",
      "Have you changed your driving habits recently?",
      "Has the vehicle been in an accident recently?",
      "Have you made any modifications to the vehicle?"
      ],
      lv: [
        "Vai transportlīdzeklis nesen ir apkopots?",
        "Vai šī problēma ir bijusi iepriekš?",
        "Vai problēma rodas īpašos laika apstākļos?",
        "Vai esat pamanījuši kādas citas simptomas?",
        "Vai nesen esat mainījuši braukšanas ieradumus?",
        "Vai transportlīdzeklis nesen ir bijis avārijā?",
        "Vai esat veikuši kādas transportlīdzekļa modifikācijas?"
      ]
    };
    
    const generalQuestionsList = generalQuestions[safeLang];
    
    while (allQuestions.length < 5) {
      const randomQuestion = generalQuestionsList[Math.floor(Math.random() * generalQuestionsList.length)];
      if (!allQuestions.includes(randomQuestion)) {
        allQuestions.push(randomQuestion);
      }
    }
    
    // Return 5-7 questions maximum
    return allQuestions.slice(0, Math.min(7, allQuestions.length));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
      if (!currentCarId) {
      toast({
        title: t('logSymptom.errors.noCarSelected') || 'No car selected',
        description: t('logSymptom.errors.noCarSelectedDesc') || 'Please select a car first',
        variant: "destructive"
      });
      navigate('/cars');
      return;
    }
    
    if (isSubmitDisabled) {
      toast({
        title: t('logSymptom.errors.missingInfo') || 'Missing information',
        description: t('logSymptom.errors.missingInfoDesc') || 'Please provide a description or attach media',
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    setProcessingStep('reading');
    
    try {
      // Get current user for image upload
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      let imageUrls: string[] = [];
      let imageMeta: Array<{ filename: string; gcsPath: string }> = [];
      
      // Small delay to show "reading" step
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Upload images using the new media hook (draft mode)
      if (imageFiles.length > 0) {
        setProcessingStep('checking');
        console.log('🖼️ Uploading images for AI analysis...');
        const uploadedFiles = await upload(imageFiles, user.id, currentCarId!);

        // Generate signed view URLs so the AI can actually "see" the images
        const signed = await Promise.all(
          uploadedFiles.map(async (u) => {
            try {
              const res = await getSignedViewUrl(u.gcsPath);
              return res.url; // use signed view URL for vision
            } catch (e) {
              console.warn('Failed to sign view URL for', u.gcsPath, e);
              return null;
            }
          })
        );

        imageUrls = signed.filter((u): u is string => !!u);
        console.log('✅ Images uploaded for AI analysis:', {
          count: imageUrls.length,
          urlsPreview: imageUrls.map(u => {
            try { return new URL(u).pathname; } catch { return 'invalid-url'; }
          })
        });

        // Build image meta for Phase 1 per-image traceability
        imageMeta = uploadedFiles.map((u) => ({ filename: u.filename, gcsPath: u.gcsPath }));
      }
      
      // Get AI analysis with image URLs (Phase 1: Questions)
      setProcessingStep('analyzing');
      console.log('�� Phase 1 payload:', { descPreview: description.slice(0, 120), imageCount: imageUrls.length });
      const phase1Result = await getAIAnalysis(description, imageUrls, imageMeta);
      
      setProcessingStep('preparing');
      setAiResponses({
        problems_stated: '', // Will be filled in Phase 2
        questions: phase1Result.questions,
        answers: Array(phase1Result.questions.length).fill(''),
        possible_causes: [], // Will be filled in Phase 2
        recommendations: [], // Will be filled in Phase 2
        urgency: 3, // Will be filled in Phase 2
        diy_check: '', // Will be filled in Phase 2
        explanation: '', // Will be filled in Phase 2
        imageObservations: phase1Result.imageObservations, // Store for Phase 2
        visionAnalysis: phase1Result.visionAnalysis,
      });
      
      // Small delay to show the preparing step
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Show AI assistant flow
      setShowAIFlow(true);
      setProcessingStep('reading'); // Reset for next time
    } catch (error) {
      console.error("Error processing submission:", error);
      setProcessingStep('reading'); // Reset on error
      toast({
        title: t('logSymptom.errors.error') || 'Error',
        description: t('logSymptom.errors.errorDesc') || 'An unexpected error occurred. Please try again.',
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleFinishSymptomLog = async () => {
    console.log('handleFinishSymptomLog called');
    console.log('Current showSummary state:', showSummary);
    
    setIsGeneratingSummary(true); // Set loading state for Phase 2
    setShowSummaryProcessingOverlay(true);
    setSummaryProcessingStep('readingAnswers');
    
    try {
      // Phase 2: Get AI summary synthesis
      console.log('🔄 Starting Phase 2: AI Summary Synthesis');
      
      setSummaryProcessingStep('analyzing');
      
      const summaryAnalysis = await getAISummary(
        description,
        aiResponses.questions,
        aiResponses.answers,
        aiResponses.imageObservations || (t('logSymptom.fallback.noImagesToAnalyze') || 'No images to analyze'),
        aiResponses.visionAnalysis
      );
      
      setSummaryProcessingStep('preparingSummary');
      
      // Update aiResponses with the summary data
      setAiResponses(prev => ({
        ...prev,
        problems_stated: summaryAnalysis.problems_stated,
        possible_causes: summaryAnalysis.possible_causes,
        recommendations: summaryAnalysis.recommendations,
        urgency: summaryAnalysis.urgency,
        diy_check: summaryAnalysis.diy_check,
        explanation: summaryAnalysis.explanation,
        symptomId: summaryAnalysis.symptomId, // Store the symptom ID
        // New fields from JSON response
        date: summaryAnalysis.date,
        time: summaryAnalysis.time,
        location: summaryAnalysis.location,
        urgencyExplanation: summaryAnalysis.urgencyExplanation,
        disclaimer: summaryAnalysis.disclaimer,
        urgency_label: summaryAnalysis.urgency_label,
        drivingSafety: summaryAnalysis.drivingSafety,
        title: summaryAnalysis.title,
      }));
      
      // Show summary and hide AI flow
      setShowSummary(true);
      setShowAIFlow(false); // Clear AI flow to show summary
      console.log('✅ Phase 2 completed, showing summary');
    } catch (error) {
      console.error('💥 Phase 2 failed:', error);
      toast({
        title: t('logSymptom.errors.summaryFailed') || 'Summary generation failed',
        description: t('logSymptom.errors.summaryFailedDesc') || 'Could not generate summary. Showing a basic summary.',
        variant: "destructive"
      });
      // Show summary anyway with available data
      setShowSummary(true);
      setShowAIFlow(false); // Clear AI flow even on error
    } finally {
      setIsGeneratingSummary(false); // Reset loading state
      setShowSummaryProcessingOverlay(false);
      setSummaryProcessingStep('readingAnswers'); // Reset for next time
    }
  };

  const handleConfirmAndSave = async () => {
    setIsSubmitting(true);
    
    try {
    // Format the selected date and time
    let dateTime = new Date();
    
    if (!useCurrentTime && customDate) {
      const [hours, minutes] = customTime.split(':').map(Number);
      dateTime = new Date(customDate);
      dateTime.setHours(hours || 0);
      dateTime.setMinutes(minutes || 0);
    }
    
      // Get current user for image upload
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Images are already uploaded during AI analysis, use the uploaded state
      let uploadedImagePaths: string[] = [];
      
      if (uploaded.length > 0) {
        console.log('🖼️ Using already uploaded images...');
        uploadedImagePaths = uploaded.map(u => u.gcsPath);
        console.log('✅ GCS paths from uploaded state:', uploadedImagePaths);
      }
      
      // Update symptom status to 'open' in database (was 'draft' after AI analysis)
      const { error: statusError } = await supabase
        .from('symptoms')
        .update({ status: 'open' })
        .eq('id', aiResponses.symptomId!);
      
      if (statusError) {
        console.warn('⚠️ Failed to update symptom status to open:', statusError);
        // Continue anyway - status update is not critical
      }
      
      // Create the issue object with GCS paths
      const issue: CarIssue = {
        id: aiResponses.symptomId!, // Use the symptomId from AI response
        title: aiResponses.title || description.slice(0, 50) + (description.length > 50 ? '...' : ''),
      description,
      date: dateTime.toISOString(),
        images: uploadedImagePaths, // Use GCS paths instead of local URLs
        videos: videoFiles.map(file => URL.createObjectURL(file)), // Keep local URLs for videos for now
        aiSummary: aiResponses.problems_stated,  // renamed from summary
      aiQuestions: aiResponses.questions,
      aiAnswers: aiResponses.answers,
        status: 'open' as const, // Changed from 'draft' to 'open' after review and confirmation
      notes: '',
        urgencyLevel: aiResponses.urgency || 3, // Use AI urgency or default
        possibleCauses: aiResponses.possible_causes,
        diyCheck: aiResponses.diy_check,
        explanation: aiResponses.explanation,
      };
      
      console.log('💾 Adding symptom to in-memory state...');
      
      // Add the issue to the car's in-memory state
      addIssueToCar(currentCarId!, issue);
    
    toast({
      title: t('logSymptom.toasts.savedTitle') || 'Issue saved',
      description: t('logSymptom.toasts.savedDesc') || 'Your car issue has been recorded',
    });
    
      console.log('✅ Symptom added to state with ID:', aiResponses.symptomId);
      
      // Clean up uploaded media after successful save
      cleanup();
      
      // Store the saved issue ID and show success dialog
      setSavedIssueId(aiResponses.symptomId!);
      setShowSuccessDialog(true);
      
    } catch (error) {
      console.error('💥 Failed to save symptom:', error);
      toast({
        title: t('logSymptom.errors.saveFailed') || 'Error saving issue',
        description: (error as any)?.message || (t('logSymptom.errors.errorDesc') || 'An unexpected error occurred. Please try again.'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAnswerChange = (index: number, value: string) => {
    const newAnswers = [...aiResponses.answers];
    newAnswers[index] = value;
    setAiResponses({
      ...aiResponses,
      answers: newAnswers,
    });
  };
  
  const handleMoreQuestions = async () => {
    setIsLoadingQuestions(true);
    
    try {
      // Get current answered questions to avoid repeats
      const existingQuestions = new Set(aiResponses.questions);
      
      // Generate more context-aware questions based on description and previous answers
      const answeredQuestionsContext = aiResponses.questions
        .map((q, i) => `Q: ${q}\nA: ${aiResponses.answers[i] || "Not answered"}`)
        .join("\n\n");
        
      const fullContext = `Problem: ${description}\n\nPrevious Q&A:\n${answeredQuestionsContext}`;
      
      // Get new AI analysis with the full context
      const analysis = await getAIAnalysis(fullContext);
      
      // Filter out duplicate questions
      const newQuestions = analysis.questions.filter(q => !existingQuestions.has(q));
      
      if (newQuestions.length === 0) {
        // If all questions are duplicates, use fallback
        const fallbackQuestions = generateFollowUpQuestions(fullContext)
          .filter(q => !existingQuestions.has(q))
          .slice(0, 3);
          
        setAiResponses({
          ...aiResponses,
          questions: [...aiResponses.questions, ...fallbackQuestions],
          answers: [...aiResponses.answers, ...Array(fallbackQuestions.length).fill('')],
        });
      } else {
        // Add new unique questions
        setAiResponses({
          ...aiResponses,
          questions: [...aiResponses.questions, ...newQuestions.slice(0, 5)], // Limit to 5 new questions
          answers: [...aiResponses.answers, ...Array(Math.min(newQuestions.length, 5)).fill('')],
        });
      }
    } catch (error) {
      console.error("Error generating more questions:", error);
      
      // Fallback to local generation if API fails
      const detectedLanguage = detectLanguage(description);
      const fallbackQuestions = generateFollowUpQuestions(description, detectedLanguage)
        .filter(q => !aiResponses.questions.includes(q))
        .slice(0, 3);
        
      if (fallbackQuestions.length > 0) {
        setAiResponses({
          ...aiResponses,
          questions: [...aiResponses.questions, ...fallbackQuestions],
          answers: [...aiResponses.answers, ...Array(fallbackQuestions.length).fill('')],
        });
      }
      
      toast({
        title: t('logSymptom.errors.moreQuestionsFailed') || 'Could not generate more questions',
        description: t('logSymptom.errors.moreQuestionsFailedDesc') || 'Using alternative questions',
        variant: "default"
      });
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  
  const handleSelectCar = (carId: string) => {
    selectCar(carId);
  };

  // Debug logging
  useEffect(() => {
    console.log('Render state:', { showAIFlow, showSummary, carsLength: cars.length });
  }, [showAIFlow, showSummary, cars.length]);

  return (
    <>
    <SymptomProcessingOverlay
      isVisible={isSubmitting}
      currentStep={processingStep}
      hasImages={imageFiles.length > 0}
      mode="initial"
    />
    <SymptomProcessingOverlay
      isVisible={showSummaryProcessingOverlay}
      currentStep={summaryProcessingStep}
      mode="summary"
    />
    <PageLayout 
      title={t('logSymptom.title') || 'Add an Issue'} 
      showBackButton 
      onBack={() => navigate(-1)}
    >
      {cars.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4">{t('logSymptom.noCarsMessage') || 'You need to add a car before logging a symptom'}</p>
            <Button 
              onClick={() => navigate('/cars/add')}
              className="bg-lime text-black hover:bg-lime/90"
            >
              {t('logSymptom.addFirstCar') || 'Add Your First Car'}
            </Button>
          </div>
        ) : showAIFlow ? (
          <AIQuestionFlow 
            description={description}
            problemsStated={aiResponses.problems_stated}
            questions={aiResponses.questions}
            answers={aiResponses.answers}
            onAnswerChange={handleAnswerChange}
            onMoreQuestions={handleMoreQuestions}
            onFinish={handleFinishSymptomLog}
            onBack={() => setShowAIFlow(false)}
            isLoadingQuestions={isLoadingQuestions}
            isGeneratingSummary={isGeneratingSummary}
          />
        ) : showSummary ? (
          <div className="space-y-6 animate-fade-in pb-10">
            <Card className="bg-soft-blue/10 border-soft-blue/20 p-6">
              <h2 className="text-xl font-bold mb-4">{t('logSymptom.summary.title') || 'Summary'}</h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('logSymptom.summary.description') || 'Review the symptom entry before saving. You can go back to edit any details.'}
              </p>
            </Card>
            
            {/* Summary Details */}
            <Card className="p-6">
              <h3 className="font-semibold mb-3">{t('logSymptom.summary.details') || 'Summary Details'}</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p><strong>{t('logSymptom.summary.date') || 'Date:'}</strong> {aiResponses.date || (t('logSymptom.summary.today') || 'Today')}</p>
                  <p><strong>{t('logSymptom.summary.time') || 'Time:'}</strong> {aiResponses.time || (t('logSymptom.summary.now') || 'Now')}</p>
                </div>
                <div>
                  <p><strong>{t('logSymptom.summary.location') || 'Location:'}</strong> {aiResponses.location || (t('logSymptom.summary.na') || 'N/A')}</p>
                  <p><strong>{t('logSymptom.summary.urgency') || 'Urgency:'}</strong> {aiResponses.urgency_label || (t('logSymptom.summary.medium') || 'Medium')}</p>
                </div>
              </div>
              
              {/* Driving Safety Display */}
              {aiResponses.drivingSafety && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-800 mb-2">{t('logSymptom.summary.drivingSafety') || 'Driving Safety'}</h4>
                  <p className="text-gray-600">{aiResponses.drivingSafety}</p>
                </div>
              )}
              
              <h4 className="mt-4 font-medium">{t('logSymptom.summary.problem') || 'Problem'}</h4>
              <p className="text-gray-700 mb-4">{aiResponses.problems_stated || (t('logSymptom.summary.aiGenerating') || 'AI analysis will be generated when you continue...')}</p>

              {aiResponses.possible_causes && aiResponses.possible_causes.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-800 mb-2">{t('logSymptom.summary.possibleCauses') || 'Possible Causes'}</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {aiResponses.possible_causes.map((cause, index) => (
                      <li key={index}>{cause}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {aiResponses.explanation && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-800 mb-2">{t('logSymptom.summary.explanation') || 'Explanation'}</h4>
                  <p className="text-gray-600">{aiResponses.explanation}</p>
                </div>
              )}
              
              {aiResponses.diy_check && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-800 mb-2">{t('logSymptom.summary.diyCheck') || 'DIY Check Items'}</h4>
                  {Array.isArray(aiResponses.diy_check) ? (
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      {aiResponses.diy_check.map((check, index) => (
                        <li key={index}>{check}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600">{aiResponses.diy_check}</p>
                  )}
                </div>
              )}
              
              {aiResponses.urgency && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-800 mb-2">{t('logSymptom.summary.urgencyLevel') || 'Urgency Level'}</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className={`w-4 h-4 rounded-full ${
                            level <= aiResponses.urgency! 
                              ? 'bg-red-500' 
                              : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {aiResponses.urgency_label || 
                        (aiResponses.urgency === 1 && (t('logSymptom.summary.low') || 'Low')) ||
                        (aiResponses.urgency === 2 && (t('logSymptom.summary.low') || 'Low')) ||
                        (aiResponses.urgency === 3 && (t('logSymptom.summary.medium') || 'Medium')) ||
                        (aiResponses.urgency === 4 && (t('logSymptom.summary.high') || 'High')) ||
                        (aiResponses.urgency === 5 && (t('logSymptom.summary.high') || 'High'))}
                    </span>
                  </div>
                </div>
              )}
            </Card>
            
            {/* Images Preview */}
            {imageFiles.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold mb-3">{t('logSymptom.summary.uploadedImages') || 'Uploaded Images'}</h3>
                <div className="flex gap-4">
                  {imageFiles.map((file, idx) => (
                    <div key={idx} className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer" onClick={() => {
                      setCurrentImageIndex(idx);
                      setFullScreenImage(URL.createObjectURL(file));
                      setImageZoom(1);
                      setImageOffset({ x: 0, y: 0 });
                    }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`preview-${idx}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Disclaimer */}
            <Card className="p-4 bg-yellow-50 border-yellow-200">
              <div className="flex items-start gap-3">
                <div className="text-yellow-600 mt-0.5">⚠️</div>
                <div>
                  <h4 className="font-medium text-yellow-800 mb-1">{t('logSymptom.summary.disclaimer.title') || 'Important Warning'}</h4>
                  <p className="text-sm text-yellow-700">
                    {aiResponses.disclaimer || (t('logSymptom.summary.disclaimer.text') || "Remember! This is not a diagnostic tool. Only a service center can determine the actual problem. This analysis is for informational purposes only and must not replace professional diagnosis.")}
                  </p>
                </div>
              </div>
            </Card>
            
            {/* Action Buttons */}
            <div className="flex gap-4 pt-6">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowSummary(false)}
              >
                {t('logSymptom.summary.edit') || 'Edit'}
              </Button>
              
              <Button 
                className="flex-1 bg-lime text-black hover:bg-lime/90"
                onClick={handleConfirmAndSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? (t('logSymptom.summary.saving') || 'Saving...') : (t('logSymptom.summary.save') || 'Save')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            {/* Car Information Section with Dropdown */}
            {selectedCar && (
              <div className="mt-8 mb-0 flex justify-center">
                <div className="w-full max-w-xs">
                  <Label htmlFor="car-select" className="mb-1 block"></Label>
                  <Select
                    value={currentCarId}
                    onValueChange={handleSelectCar}
                  >
                    <SelectTrigger id="car-select">
                      <SelectValue placeholder={t('logSymptom.selectCarPlaceholder') || 'Select a car'} />
                    </SelectTrigger>
                    <SelectContent>
                      {cars.map((car) => (
                        <SelectItem key={car.id} value={car.id}>
                          {car.nickname || `${car.brand} ${car.model}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <div className="w-full max-w-xl">
                <div className="rounded-[20px] bg-white p-6 pt-0">
                  <div className="space-y-2 mb-4">
                    <h1 className="text-xl font-bold mt-0 pt-0 mb-4" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '20px', lineHeight: '28px', letterSpacing: '0.025em' }}>
                      {t('logSymptom.heroTitle') || "What's wrong?! I'll help you figure it out."}
                    </h1>
                    <p
                      style={{
                        color: '#333333',
                        fontSize: 14,
                        fontFamily: 'Lato',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: 0.10,
                        wordWrap: 'break-word',
                        marginTop: 0,
                        paddingTop: 0
                      }}
                    >
                      {t('logSymptom.heroDescription') || "Go ahead and describe in your own words. We'll take it one step at a time."}
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Location Display */}
                    <div className="flex items-center justify-between text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span>📍</span>
                        {userLocation ? (
                          <span>
                            {t('logSymptom.location.reportingFrom') || 'Reporting from:'} <strong>{userLocation.city}, {userLocation.country}</strong>
                            {isGettingLocation && <span className="ml-2">{t('logSymptom.location.detectingShort') || '(detecting...)'}</span>}
                          </span>
                        ) : (
                          <span>
                            {isGettingLocation ? (t('logSymptom.location.detecting') || 'Detecting your location...') : (t('logSymptom.location.notAvailable') || 'Location not available')}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setUserLocation(null);
                          setIsGettingLocation(true);
                          getCurrentLocation().then(setUserLocation).catch(console.warn).finally(() => setIsGettingLocation(false));
                        }}
                        disabled={isGettingLocation}
                        className="h-7 px-2 text-xs"
                      >
                        {isGettingLocation ? '...' : (t('logSymptom.location.refresh') || 'Refresh')}
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description"></Label>
                      <Textarea
                        id="description"
                        placeholder={t('logSymptom.description.placeholder') || "Describe the issue in your native language... e.g., 'When I turn the wheel I hear a whining sound' or 'Când pornesc mașina, motorul face un zgomot ciudat'"}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="min-h-[120px] resize-y"
                        style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}
                      />
                    </div>
                    

                    <div className="space-y-2">
                      <Label
                        style={{
                          color: '#333333',
                          fontSize: 14,
                          fontFamily: 'Lato',
                          fontWeight: 400,
                          lineHeight: '20px',
                          letterSpacing: 0.10,
                          wordWrap: 'break-word'
                        }}
                      >
                        {t('logSymptom.images.label') || 'Do you have any images that could help me see the problem better? Upload images here: (up to 3 images)'}
                      </Label>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 16, marginBottom: 0 }}>
                        {imageFiles.slice(0, 3).map((file, idx) => (
                          <div key={idx} style={{ position: 'relative', width: 100, height: 76, borderRadius: 10.18, overflow: 'hidden', boxShadow: '0px 10px 8px -1px #EEEEEE', background: '#FDFDFD', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => {
                            setCurrentImageIndex(idx);
                            setFullScreenImage(URL.createObjectURL(file));
                            setImageZoom(1);
                            setImageOffset({ x: 0, y: 0 });
                          }}>
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`preview-${idx}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveImage(idx); }}
                              style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                              <X size={14} color="#fff" />
                            </button>
                          </div>
                        ))}
                        {imageFiles.length < 3 && (
                          <div
                            style={{width: 100, height: 76, paddingLeft: 34, paddingRight: 34, paddingTop: 22, paddingBottom: 22, background: '#FDFDFD', borderRadius: 10.18, justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'inline-flex', boxShadow: '0px 10px 8px -1px #EEEEEE', cursor: 'pointer'}}
                            onClick={handleAddImageClick}
                          >
                            <div style={{width: 32, height: 32, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <img src="/icons/AddCarImage.png" alt={t('logSymptom.images.addAlt') || 'Add'} style={{width: 28, height: 28, objectFit: 'contain'}} />
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          onChange={handleImageInputChange}
                          disabled={imageFiles.length >= 3}
                          multiple={imageFiles.length < 3}
                          max={3 - imageFiles.length}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        style={{
                          color: '#333333',
                          fontSize: 14,
                          fontFamily: 'Lato',
                          fontWeight: 400,
                          lineHeight: '20px',
                          letterSpacing: 0.10,
                          wordWrap: 'break-word',
                          marginBottom: 4,
                          display: 'block'
                        }}
                      >
                        {t('logSymptom.whenHappened.label') || 'When did this happen?'}
                      </Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <input
                          type="checkbox"
                          id="useCurrentTime"
                          checked={useCurrentTime}
                          onChange={() => setUseCurrentTime(!useCurrentTime)}
                          className="rounded text-lime"
                          style={{ width: 18, height: 18, accentColor: 'rgb(231 254 82 / 0.9)', backgroundColor: 'rgb(231 254 82 / 0.9)', marginRight: 6 }}
                        />
                        <Label htmlFor="useCurrentTime" className="cursor-pointer" style={{ fontFamily: 'Lato', fontSize: 14, color: '#333', fontWeight: 400, lineHeight: '20px' }}>{t('logSymptom.whenHappened.now') || 'Now'}</Label>
                      </div>
                      {!useCurrentTime && (
                        <div style={{ marginTop: 4 }}>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className={cn(
                                  "w-full inline-flex items-center whitespace-nowrap text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 tracking-[0.1px] leading-6 border-2 border-[#F0F0F0] bg-[#FDFDFD] text-black hover:bg-[#FDFDFD]/90 rounded-[8px] gap-[10px] h-12 px-6 py-3 justify-start text-left font-normal",
                                  !customDate && "text-muted-foreground"
                                )}
                                style={{ fontFamily: 'Lato', fontSize: 14, fontWeight: 400, display: 'flex', alignItems: 'center' }}
                              >
                                <CalendarIcon className="h-4 w-4" />
                                {customDate ? format(customDate, "PPP") : (t('logSymptom.whenHappened.pickDate') || 'Pick a date')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start" style={{ fontFamily: 'Lato' }}>
                              <div className="p-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                              <Calendar
                                mode="single"
                                selected={customDate}
                                onSelect={setCustomDate}
                                initialFocus
                                  className="rounded-md border-0"
                                  classNames={{
                                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                    month: "space-y-4",
                                    caption: "flex justify-center pt-1 relative items-center",
                                    caption_label: "text-sm font-medium",
                                    nav: "space-x-1 flex items-center",
                                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                                    nav_button_previous: "absolute left-1",
                                    nav_button_next: "absolute right-1",
                                    table: "w-full border-collapse space-y-1",
                                    head_row: "flex",
                                    head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
                                    row: "flex w-full mt-2",
                                    cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                                    day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground",
                                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                                    day_today: "bg-accent text-accent-foreground",
                                    day_outside: "text-muted-foreground opacity-50",
                                    day_disabled: "text-muted-foreground opacity-50",
                                    day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                                    day_hidden: "invisible"
                                  }}
                                />
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-4 pt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        className="flex-1"
                        onClick={() => navigate('/')}
                      >
                        {t('logSymptom.actions.cancel') || 'Cancel'}
                      </Button>
                      <Button 
                        type="submit" 
                        className="flex-1 bg-lime text-black hover:bg-lime/90"
                        disabled={isSubmitDisabled || isSubmitting}
                      >
                        {isSubmitting ? (t('logSymptom.actions.submitting') || 'Submitting...') : (t('logSymptom.actions.submit') || 'Submit')}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
    </PageLayout>

    {/* Full Screen Image Modal */}
    {fullScreenImage && (
      <div 
        className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
        onClick={() => {
          if (imageZoom === 1) {
            setFullScreenImage(null);
            setCurrentImageIndex(-1);
            setImageZoom(1);
            setImageOffset({ x: 0, y: 0 });
          }
        }}
      >
        <button
          onClick={() => {
            setFullScreenImage(null);
            setCurrentImageIndex(-1);
            setImageZoom(1);
            setImageOffset({ x: 0, y: 0 });
          }}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          aria-label={t('logSymptom.imageViewer.close') || 'Close'}
        >
          <X className="w-8 h-8" />
        </button>

        {/* Previous Button */}
        {imageFiles.length > 1 && currentImageIndex > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newIndex = currentImageIndex - 1;
              setCurrentImageIndex(newIndex);
              setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
            aria-label={t('logSymptom.imageViewer.previous') || 'Previous image'}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Next Button */}
        {imageFiles.length > 1 && currentImageIndex < imageFiles.length - 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newIndex = currentImageIndex + 1;
              setCurrentImageIndex(newIndex);
              setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
            aria-label={t('logSymptom.imageViewer.next') || 'Next image'}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Image Counter */}
        {imageFiles.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full z-10">
            {currentImageIndex + 1} / {imageFiles.length}
          </div>
        )}

        {/* Image Container with Zoom and Pan */}
        <div
          className="relative w-full h-full flex items-center justify-center touch-none"
          onWheel={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
            }
          }}
          onMouseDown={(e) => {
            if (imageZoom > 1 && e.button === 0) {
              panStartRef.current = { x: e.clientX - imageOffset.x, y: e.clientY - imageOffset.y };
            }
          }}
          onMouseMove={(e) => {
            if (panStartRef.current && imageZoom > 1) {
              setImageOffset({
                x: e.clientX - panStartRef.current.x,
                y: e.clientY - panStartRef.current.y,
              });
            }
          }}
          onMouseUp={() => {
            panStartRef.current = null;
          }}
          onMouseLeave={() => {
            panStartRef.current = null;
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 1 && imageZoom === 1) {
              const touch = e.touches[0];
              panStartRef.current = { x: touch.clientX, y: touch.clientY };
            }
          }}
          onTouchMove={(e) => {
            if (e.touches.length === 1 && imageZoom === 1 && panStartRef.current) {
              const touch = e.touches[0];
              const deltaX = touch.clientX - panStartRef.current.x;
              if (Math.abs(deltaX) > 50) {
                if (deltaX > 0 && currentImageIndex > 0) {
                  const newIndex = currentImageIndex - 1;
                  setCurrentImageIndex(newIndex);
                  setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
                  panStartRef.current = null;
                } else if (deltaX < 0 && currentImageIndex < imageFiles.length - 1) {
                  const newIndex = currentImageIndex + 1;
                  setCurrentImageIndex(newIndex);
                  setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
                  panStartRef.current = null;
                }
              }
            }
          }}
          onTouchEnd={() => {
            panStartRef.current = null;
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={fullScreenImage}
            alt={t('logSymptom.imageViewer.fullScreen') || 'Full screen'}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: imageZoom > 1 
                ? `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${imageZoom})`
                : 'scale(1)',
              transformOrigin: 'center center',
              cursor: imageZoom > 1 ? 'grab' : 'default',
              transition: imageZoom === 1 ? 'transform 0.2s' : 'none',
            }}
            draggable={false}
          />
        </div>

        {/* Zoom Indicator */}
        {imageZoom > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 px-3 py-1 rounded-full z-10">
            {Math.round(imageZoom * 100)}%
          </div>
        )}
      </div>
    )}
    
    {/* Success Dialog */}
    <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
      <DialogContent className="sm:max-w-[360px] p-6">
        <div className="text-center">
          <img src="/icons/Success.png" alt="Success" className="w-12 h-12 mx-auto mb-3" />
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold mb-2">
              {t('logSymptom.success.title') || 'Issue successfully saved'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-6">
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                navigate(`/records/${currentCarId}?tab=issues`);
              }}
              className="w-full bg-[#e7fe52] text-black hover:bg-[#e7fe52]/90"
            >
              {t('logSymptom.success.close') || 'Close'}
            </Button>
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                if (savedIssueId && currentCarId) {
                  navigate(`/symptom-summary/${currentCarId}/${savedIssueId}`);
                }
              }}
              variant="secondary"
              className="w-full"
            >
              {t('logSymptom.success.viewIssue') || 'View issue'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default LogSymptomPage;

